'use client'

import clsx from 'clsx'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { selectors } from 'store/selectors'

import { useIsClient } from '@/shared/lib/hooks/useIsClient'
import { useVisibleItems } from '@/shared/lib/hooks/useVisibleItems'
import { TBurgerLevel1 } from '@/shared/types/deprecated-types'
import { Button, UnderlineLink } from '@/shared/ui'
import { Tooltip } from '@/shared/ui/react-tooltip/react-tooltip'

import { DotsIcon } from '../assets/dots-icon'
import { loyaltyMenuItems, otherMenuItems } from '../data'
import { Popup } from '../popup/popup'

import { BORDER_RIGHT_CATEGORY_NAMES, GOLD_GRADIENT_CATEGORY_NAMES, PURPLE_GRADIENT_CATEGORY_NAMES } from './constants'

import cl from './style.module.scss'

export const HeaderCategories = (): JSX.Element => {
	const isClient = useIsClient()
	const items = useSelector(selectors.selectHeaderCategories)

	const [windowWidth, setWindowWidth] = useState<number | null>(null)

	useEffect(() => {
		setWindowWidth(typeof window !== 'undefined' ? window?.innerWidth : null)
	}, [])

	const additionalMenuItems =
		windowWidth && windowWidth < 1200
			? [...loyaltyMenuItems, ...otherMenuItems].map(item => ({
					ID: item.code || item.link,
					SECTION_LINK: item.link,
					NAME: item.title,
					SECTION_ID: item.code || item.link,
					SUB_MENU: []
			  }))
			: []

	const allItems = [...items, ...additionalMenuItems]

	const [activeCat, setActiveCat] = useState(null)
	const [isActiveModal, setIsActiveModal] = useState(false)
	const [modalContent, setModalContent] = useState<TBurgerLevel1>(null)

	const { visibleItems, hiddenItems } = useVisibleItems({
		items: allItems,
		breakpoints: [
			{ width: 1100, count: 9 },
			{ width: 1200, count: 10 },
			{ width: 1400, count: 11 },
			{ width: 1500, count: 12 }
		]
	})

	const handleMouseOver = (id: string) => {
		setActiveCat(id)
		const content = items?.find(item => item?.ID === id) || null
		if (content?.SUB_MENU.length > 0) {
			setModalContent(content)
			setIsActiveModal(true)
		}
	}

	const handleMouseLeave = () => {
		setActiveCat(null)
		setIsActiveModal(false)
	}

	return (
		<>
			<nav className={cl.nav} onMouseLeave={handleMouseLeave}>
				<ul className={cl.list}>
					{visibleItems.map(({ ID, SECTION_LINK, NAME }) => (
						<li className={cl.item} key={ID}>
							<UnderlineLink
								href={SECTION_LINK}
								data-tooltip-id="tooltip"
								onMouseEnter={isClient ? () => handleMouseOver(ID) : undefined}
								active={ID === activeCat}
								underlineOffset={-11}
								className={clsx(cl.link, {
									[cl.link_gradientPurple]: PURPLE_GRADIENT_CATEGORY_NAMES.includes(NAME),
									[cl.link_gradientGold]: GOLD_GRADIENT_CATEGORY_NAMES.includes(NAME),
									[cl.link_border_right]: BORDER_RIGHT_CATEGORY_NAMES.includes(NAME)
								})}>
								{NAME}
							</UnderlineLink>
						</li>
					))}

					{hiddenItems.length > 0 && (
						<>
							<Button
								theme={'clear'}
								id={'extra-items'}
								data-tooltip-id={'#extra-items'}
								className={cl.btn}
								onMouseEnter={() => {
									setActiveCat(null)
									setIsActiveModal(false)
								}}>
								<span style={{ fontSize: `0`, lineHeight: `0`, visibility: `hidden` }}>Показать еще</span>
								<DotsIcon />
							</Button>
							<Tooltip
								id={'#extra-items'}
								place={'bottom-start'}
								offset={14}
								noArrow={true}
								clickable={true}
								className={clsx(cl.tooltip, cl.extra)}>
								{hiddenItems.map(item => {
									return (
										<Link prefetch={false} key={item.SECTION_LINK} href={item.SECTION_LINK} id={item.SECTION_ID}>
											{item.NAME}
										</Link>
									)
								})}
							</Tooltip>
						</>
					)}
				</ul>
				<div className={cl.rapper}>
					<div className={clsx(cl.wrapper, { [cl.wrapper_active]: isActiveModal })} onClick={handleMouseLeave}>
						<Popup content={modalContent} />
					</div>
				</div>
			</nav>
		</>
	)
}
